import React from 'react';
import { useLanguage } from '../i18n/LanguageContext';
import { track } from '../lib/analytics';
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuCheckboxItem,
  DropdownMenuSeparator,
} from './ui/dropdown-menu';
import { Slider } from './ui/slider';
import { 
  Compass, 
  MapPin, 
  Clock, 
  Globe, 
  DollarSign,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  X,
  Search
} from 'lucide-react';

/**
 * FilterBar - Reusable filter component for experience sections
 * 
 * @param {Object} filters - Current filter values
 * @param {Function} onFilterChange - Callback when filters change
 * @param {Array} categories - Available categories for the category dropdown
 * @param {Array} availableLanguages - Dynamic list of languages from experiences
 */
const FilterBarInner = ({ 
  filters = {}, 
  onFilterChange,
  categories = [],
  availableLanguages = [],
  clearAllSignal = 0
}) => {
  const { t } = useLanguage();
  const [openDropdown, setOpenDropdown] = React.useState(null);
  const pointerDownRef = React.useRef(null);
  const touchStartRef = React.useRef({ x: 0, y: 0 });
  const isScrollingRef = React.useRef(false);
  // Mobile horizontal scroll container (the parent flex row that wraps the
  // filter dropdowns + category pills). Desktop has a separate inner
  // scroll container `pillsScrollRef`. We reset both whenever a category
  // is selected/deselected so the user always sees the chosen pill.
  const outerScrollRef = React.useRef(null);

  // Define all 8 categories — labels resolved via translation keys
  const allCategories = [
    { slug: 'events',            labelKey: 'cat_events', isEvents: true },
    { slug: 'tours_sightseeing', labelKey: 'cat_tours_sightseeing' },
    { slug: 'food_wine',         labelKey: 'cat_food_wine' },
    { slug: 'water_activities',  labelKey: 'cat_water_activities' },
    { slug: 'beaches',           labelKey: 'cat_beaches' },
    { slug: 'culture_arts',      labelKey: 'cat_culture_arts' },
    { slug: 'nightlife',         labelKey: 'cat_nightlife' },
    { slug: 'romantic',          labelKey: 'cat_romantic_experiences' },
    { slug: 'transfers',         labelKey: 'cat_transfers' }
  ];

  // Ref to the mobile search input so we can programmatically blur it
  // (dismiss the on-screen keyboard) on Enter, vertical scroll, or
  // horizontal scroll of the filter pills row.
  const searchInputRef = React.useRef(null);

  const blurSearchInput = React.useCallback(() => {
    if (
      searchInputRef.current &&
      document.activeElement === searchInputRef.current
    ) {
      searchInputRef.current.blur();
    }
  }, []);

  // Mobile keyboard dismiss — listen ONLY to `touchmove` (real finger
  // motion) so layout-induced reflows (browser address bar appearing,
  // keyboard opening) don't accidentally blur the input the user just
  // tapped. `touchmove` fires on the very first finger drag, before the
  // browser's own scroll threshold kicks in.
  React.useEffect(() => {
    window.addEventListener('touchmove', blurSearchInput, { passive: true });
    return () => window.removeEventListener('touchmove', blurSearchInput);
  }, [blurSearchInput]);

  // Get display value for search input
  const getSearchDisplayValue = () => {
    return filters.search || '';
  };

  const handleFilterChange = (key, value) => {
    if (onFilterChange) {
      onFilterChange({ ...filters, [key]: value });
    }
  };

  // Track pointer down
  const handlePointerDown = (name) => (e) => {
    e.preventDefault(); // Prevent Radix from opening on pointer down
    pointerDownRef.current = name;
  };

  // Handle click - toggle dropdown after full press+release
  const handleClick = (name) => (e) => {
    if (pointerDownRef.current === name) {
      setOpenDropdown(prev => prev === name ? null : name);
    }
    pointerDownRef.current = null;
  };

  // Handle Radix's close events (outside clicks, escape key, etc.)
  const handleOpenChange = (name) => (open) => {
    if (!open) {
      setOpenDropdown(null);
      pointerDownRef.current = null; // Reset on close
    }
  };

  // Clear individual filters
  const clearCategories = () => handleFilterChange('categories', []);
  const clearLocations = () => handleFilterChange('locations', []);
  const clearDurations = () => handleFilterChange('durations', []);
  const clearLanguages = () => handleFilterChange('languages', []);
  const clearPriceRange = () => handleFilterChange('priceRange', [0, 200]);

  // Single-select category: tapping the active one clears it, tapping a
  // different one replaces the previous selection. Either way, the search
  // input is also cleared so the two filter modes (search vs. category)
  // never compete with each other.
  const handleCategoryToggle = (categoryValue) => {
    const currentCategories = filters.categories || [];
    const isActive = currentCategories.includes(categoryValue);
    const newCategories = isActive ? [] : [categoryValue];
    if (!isActive) {
      track('category_filter_click', { slug: categoryValue });
    }
    if (onFilterChange) {
      onFilterChange({ ...filters, categories: newCategories, search: '' });
    }
    // Scroll the horizontal pill row back to the start so the user always
    // sees the freshly-selected pill (it gets sorted to position 0 on
    // re-render). Works for both mobile (outer scroll) and desktop
    // (inner pills scroll container).
    requestAnimationFrame(() => {
      outerScrollRef.current?.scrollTo({ left: 0, behavior: 'instant' });
      pillsScrollRef.current?.scrollTo({ left: 0, behavior: 'instant' });
    });
  };

  // Handle multi-select for location
  const handleLocationToggle = (locationValue) => {
    const currentLocations = filters.locations || [];
    let newLocations;
    
    if (currentLocations.includes(locationValue)) {
      newLocations = currentLocations.filter(l => l !== locationValue);
    } else {
      newLocations = [...currentLocations, locationValue];
    }
    
    handleFilterChange('locations', newLocations);
    // Auto-close the dropdown after a selection (mobile + desktop) so the
    // user immediately sees the filtered results.
    setOpenDropdown(null);
  };

  // Handle multi-select for duration
  const handleDurationToggle = (durationValue) => {
    const currentDurations = filters.durations || [];
    let newDurations;
    
    if (currentDurations.includes(durationValue)) {
      newDurations = currentDurations.filter(d => d !== durationValue);
    } else {
      newDurations = [...currentDurations, durationValue];
    }
    
    handleFilterChange('durations', newDurations);
  };

  // Handle multi-select for language
  const handleLanguageToggle = (languageValue) => {
    const currentLanguages = filters.languages || [];
    let newLanguages;
    
    if (currentLanguages.includes(languageValue)) {
      newLanguages = currentLanguages.filter(l => l !== languageValue);
    } else {
      newLanguages = [...currentLanguages, languageValue];
    }
    
    handleFilterChange('languages', newLanguages);
  };

  // Get display text for multi-select dropdowns
  const getCategoryDisplayText = () => {
    const selected = filters.categories || [];
    if (selected.length === 0) return 'Category';
    if (selected.length === 1) return '1 selected';
    return `${selected.length} selected`;
  };

  const getLocationDisplayText = () => {
    const selected = filters.locations || [];
    if (selected.length === 0) return t('location');
    if (selected.length === 1) {
      const locationMap = {
        sitges: t('loc_sitges'),
        barcelona: t('loc_barcelona'),
        tarragona: t('loc_tarragona'),
        day_trips: t('loc_day_trips'),
      };
      return locationMap[selected[0]] || t('location');
    }
    return `${selected.length} selected`;
  };

  const getDurationDisplayText = () => {
    const selected = filters.durations || [];
    if (selected.length === 0) return 'Duration';
    if (selected.length === 1) {
      const durationMap = { '0-3': '0-3 hours', '3-6': '3-6 hours', '6+': '6+ hours' };
      return durationMap[selected[0]] || 'Duration';
    }
    return `${selected.length} selected`;
  };

  const getLanguageDisplayText = () => {
    const selected = filters.languages || [];
    if (selected.length === 0) return 'Language';
    if (selected.length === 1) {
      const langMap = { english: 'English', spanish: 'Spanish', catalan: 'Catalan', french: 'French', german: 'German' };
      return langMap[selected[0]] || 'Language';
    }
    return `${selected.length} selected`;
  };

  // Use effect to add global scroll listener
  React.useEffect(() => {
    const handleWindowScroll = () => {
      if (openDropdown) {
        setOpenDropdown(null);
      }
    };
    
    window.addEventListener('scroll', handleWindowScroll, { passive: true });
    
    return () => {
      window.removeEventListener('scroll', handleWindowScroll);
    };
  }, [openDropdown]);

  // ─── Desktop-only: horizontal scroll state for category pills ───
  const pillsScrollRef = React.useRef(null);
  const [canScrollLeft, setCanScrollLeft] = React.useState(false);
  const [canScrollRight, setCanScrollRight] = React.useState(false);

  const updateScrollShadows = React.useCallback(() => {
    const el = pillsScrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 2);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 2);
  }, []);

  React.useEffect(() => {
    updateScrollShadows();
    const el = pillsScrollRef.current;
    if (!el) return;
    el.addEventListener('scroll', updateScrollShadows, { passive: true });
    window.addEventListener('resize', updateScrollShadows);

    // Re-measure when the scroll container's content size changes
    // (e.g. pill labels re-render in a different language with different widths).
    let ro;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(updateScrollShadows);
      ro.observe(el);
      // Also observe the first child so we react to scrollWidth changes
      // even when the container's own clientWidth stays the same.
      if (el.firstElementChild) ro.observe(el.firstElementChild);
    }

    return () => {
      el.removeEventListener('scroll', updateScrollShadows);
      window.removeEventListener('resize', updateScrollShadows);
      if (ro) ro.disconnect();
    };
  }, [updateScrollShadows]);

  const scrollPills = (dir) => {
    const el = pillsScrollRef.current;
    if (!el) return;
    // Jump to the very start or end in one click.
    const target = dir > 0 ? el.scrollWidth : 0;
    el.scrollTo({ left: target, behavior: 'instant' });
  };

  // Reset the horizontal scroll position to the start whenever the parent
  // bumps `clearAllSignal` (i.e. user pressed "Clear all filters"). Skips
  // the initial render so a fresh mount with all-empty filters doesn't
  // trigger a redundant scroll-to-0 on already-zero containers.
  const clearAllInitialRef = React.useRef(true);
  React.useEffect(() => {
    if (clearAllInitialRef.current) {
      clearAllInitialRef.current = false;
      return;
    }
    requestAnimationFrame(() => {
      outerScrollRef.current?.scrollTo({ left: 0, behavior: 'instant' });
      pillsScrollRef.current?.scrollTo({ left: 0, behavior: 'instant' });
    });
  }, [clearAllSignal]);

  return (
    <div>
      {/* Mobile Search Bar - only on secondary pages */}
      <div className="lg:hidden mb-3 px-5">
        <div className="relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-[#2d5f8d] pointer-events-none" />
          <input
            ref={searchInputRef}
            type="text"
            placeholder={t('search_experiences')}
            value={getSearchDisplayValue()}
            onFocus={(e) => e.target.select()}
            onChange={(e) => {
              const value = e.target.value;
              if (onFilterChange) {
                // Typing in search clears any active category so the two
                // filter modes never compete with each other.
                onFilterChange({ ...filters, search: value, categories: [] });
              }
            }}
            onKeyDown={(e) => {
              // Pressing Enter on mobile dismisses the on-screen keyboard.
              if (e.key === 'Enter') {
                e.preventDefault();
                blurSearchInput();
              }
            }}
            enterKeyHint="search"
            className="w-full h-[50px] pl-11 pr-11 text-[15px] font-bold text-[#1a4d7a] rounded-full border border-[#1a4d7a] bg-white
                       placeholder:text-[#2d5f8d]/60 placeholder:font-medium
                       focus:outline-none focus:ring-2 focus:ring-[#0184ff]/20 focus:border-[#0184ff]
                       transition-all duration-200 shadow-sm"
            style={{ fontFamily: "'Outfit', sans-serif" }}
          />
          {getSearchDisplayValue() && (
            <button
              type="button"
              data-testid="filterbar-search-clear"
              onClick={() => handleFilterChange('search', '')}
              aria-label="Clear search"
              className="absolute right-3 top-1/2 -translate-y-1/2 h-7 w-7 rounded-full
                         flex items-center justify-center text-[#2d5f8d]
                         hover:bg-[#eaf3fa] hover:text-[#0184ff]
                         transition-colors duration-150"
            >
              <X className="h-4 w-4" strokeWidth={2.5} />
            </button>
          )}
        </div>
      </div>

      {/* Filter Buttons */}
      <div
        ref={outerScrollRef}
        onScroll={(e) => {
          // Dismiss any open filter dropdown the moment the user starts
          // horizontally scrolling the pills row — keeps the dropdown panel
          // anchored to its trigger and prevents it from drifting off.
          if (openDropdown) setOpenDropdown(null);
          blurSearchInput(e);
        }}
        className="flex gap-2 lg:gap-3 overflow-x-auto lg:overflow-visible scrollbar-hide pl-5 pr-5 lg:pl-0 lg:pr-0 lg:items-center"
      >
        
        {/* Location Filter */}
        <DropdownMenu modal={false} open={openDropdown === 'location'} onOpenChange={handleOpenChange('location')}>
          <DropdownMenuTrigger asChild>
            <button 
              onPointerDown={handlePointerDown('location')}
              onClick={handleClick('location')}
              className="h-10 px-3 lg:px-5 lg:min-w-[140px] rounded-full border border-[#1a4d7a] bg-white hover:bg-blue-50 hover:border-[#0184ff] flex items-center justify-between gap-1 text-sm font-bold text-[#1a4d7a] transition-all shadow-sm hover:shadow whitespace-nowrap shrink-0"
              style={{ fontFamily: "'Outfit', sans-serif" }}
            >
              <div className="flex items-center gap-2">
                <span>{getLocationDisplayText()}</span>
              </div>
              <div className="flex items-center gap-2">
                {(filters.locations || []).length > 0 ? (
                  <span
                    role="button"
                    aria-label="Clear location filter"
                    data-testid="clear-location-filter"
                    onPointerDown={(e) => { e.stopPropagation(); }}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      clearLocations();
                    }}
                    className="inline-flex items-center justify-center h-5 w-5 rounded-full hover:bg-blue-100 transition-colors cursor-pointer"
                  >
                    <X className="h-4 w-4 text-[#2d5f8d]" />
                  </span>
                ) : (
                  <ChevronDown className="h-4 w-4 text-[#2d5f8d]" />
                )}
              </div>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-56">
            <DropdownMenuCheckboxItem
              className="text-sm font-bold text-[#1a4d7a]"
              style={{ fontFamily: "'Outfit', sans-serif" }}
              checked={(filters.locations || []).includes('sitges')}
              onCheckedChange={() => handleLocationToggle('sitges')}
            >
              {t('loc_sitges')}
            </DropdownMenuCheckboxItem>
            <DropdownMenuCheckboxItem
              className="text-sm font-bold text-[#1a4d7a]"
              style={{ fontFamily: "'Outfit', sans-serif" }}
              checked={(filters.locations || []).includes('barcelona')}
              onCheckedChange={() => handleLocationToggle('barcelona')}
            >
              {t('loc_barcelona')}
            </DropdownMenuCheckboxItem>
            <DropdownMenuCheckboxItem
              className="text-sm font-bold text-[#1a4d7a]"
              style={{ fontFamily: "'Outfit', sans-serif" }}
              checked={(filters.locations || []).includes('tarragona')}
              onCheckedChange={() => handleLocationToggle('tarragona')}
            >
              {t('loc_tarragona')}
            </DropdownMenuCheckboxItem>
            <DropdownMenuCheckboxItem
              className="text-sm font-bold text-[#1a4d7a]"
              style={{ fontFamily: "'Outfit', sans-serif" }}
              checked={(filters.locations || []).includes('day_trips')}
              onCheckedChange={() => handleLocationToggle('day_trips')}
            >
              {t('loc_day_trips')}
            </DropdownMenuCheckboxItem>
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Category Pills — wrapped in desktop-only scroll container with arrows + fade edges.
            On mobile this wrapper is `contents` so pills flow in the outer scroll. */}
        <div className="contents lg:contents">
        <div className="contents lg:block lg:flex-1 lg:min-w-0 lg:relative">
          <div
            ref={pillsScrollRef}
            onScroll={() => {
              // Close any open filter dropdown when the desktop pills row
              // is scrolled horizontally so the panel doesn't float over a
              // different trigger button.
              if (openDropdown) setOpenDropdown(null);
            }}
            className="contents lg:flex lg:gap-3 lg:overflow-x-auto scrollbar-hide lg:flex-nowrap"
            style={{
              // Desktop-only fade mask: hides partial pills near the scroll arrows
              // without drawing any visible overlay rectangle. The arrows sit
              // outside this scroll container, so they stay fully opaque.
              WebkitMaskImage: (canScrollLeft || canScrollRight)
                ? `linear-gradient(to right, ${canScrollLeft ? 'transparent 0, transparent 24px, black 56px' : 'black 0'}, ${canScrollRight ? 'black calc(100% - 56px), transparent calc(100% - 24px), transparent 100%' : 'black 100%'})`
                : undefined,
              maskImage: (canScrollLeft || canScrollRight)
                ? `linear-gradient(to right, ${canScrollLeft ? 'transparent 0, transparent 24px, black 56px' : 'black 0'}, ${canScrollRight ? 'black calc(100% - 56px), transparent calc(100% - 24px), transparent 100%' : 'black 100%'})`
                : undefined,
            }}
          >
            {/* Individual Category Buttons — Events pill is pinned at
                position 0. The remaining pills still sort selected-to-front
                among themselves so the user's active choice is always
                visible next to Events. */}
            {(() => {
              const [eventsCat, ...otherCats] = allCategories;
              const sel = filters.categories || [];
              const sortedOthers = [...otherCats].sort((a, b) => {
                const aSel = sel.includes(a.slug);
                const bSel = sel.includes(b.slug);
                if (aSel === bSel) return 0;
                return aSel ? -1 : 1;
              });
              return [eventsCat, ...sortedOthers];
            })()
              .map((cat) => {
              const isActive = (filters.categories || []).includes(cat.slug);
              return (
                <button
                  key={cat.slug}
                  onTouchStart={(e) => {
                    const touch = e.touches[0];
                    touchStartRef.current = { x: touch.clientX, y: touch.clientY };
                    isScrollingRef.current = false;
                  }}
                  onTouchMove={(e) => {
                    const touch = e.touches[0];
                    const deltaX = Math.abs(touch.clientX - touchStartRef.current.x);
                    const deltaY = Math.abs(touch.clientY - touchStartRef.current.y);
                    if (deltaX > 10 || deltaY > 10) {
                      isScrollingRef.current = true;
                    }
                  }}
                  onTouchEnd={(e) => {
                    if (!isScrollingRef.current) {
                      e.preventDefault();
                      handleCategoryToggle(cat.slug);
                      e.currentTarget.blur();
                    }
                  }}
                  onClick={(e) => {
                    if (!('ontouchstart' in window)) {
                      handleCategoryToggle(cat.slug);
                      e.currentTarget.blur();
                    }
                  }}
                  className={`h-10 px-4 lg:px-5 rounded-full text-sm font-bold transition-all shadow-sm hover:shadow whitespace-nowrap shrink-0 outline-none focus:outline-none focus-visible:outline-none active:outline-none touch-none ${
                    isActive
                      ? (cat.isEvents
                          ? 'bg-[#e11d48] text-white hover:bg-[#be123c]'
                          : 'bg-[#3c84ff] text-white hover:bg-[#2a6fe0]')
                      : (cat.isEvents
                          ? 'bg-[#fee2e2] text-[#b91c1c] lg:hover:bg-[#fecaca]'
                          : 'bg-[#e6f2ff] text-[#1a4d7a] lg:hover:bg-[#d0e7ff]')
                  }`}
                  style={{ fontFamily: "'Outfit', sans-serif", WebkitTapHighlightColor: 'transparent', touchAction: 'manipulation', outline: 'none' }}
                >
                  {t(cat.labelKey)}
                </button>
              );
            })}
          </div>

          {/* Left arrow (desktop only, only when scrolled) */}
          {canScrollLeft && (
            <button
              type="button"
              onClick={() => scrollPills(-1)}
              aria-label="Scroll categories left"
              data-testid="filterbar-scroll-left"
              className="hidden lg:flex absolute left-0 top-1/2 -translate-y-1/2 h-10 w-10 items-center justify-center rounded-full bg-[#e6f2ff] text-[#1a4d7a] hover:bg-[#d0e7ff] shadow-sm hover:shadow transition-all z-20 shrink-0"
              style={{ fontFamily: "'Outfit', sans-serif" }}
            >
              <ChevronLeft className="h-5 w-5" strokeWidth={2.5} />
            </button>
          )}

          {/* Right arrow (desktop only, only when more to scroll) */}
          {canScrollRight && (
            <button
              type="button"
              onClick={() => scrollPills(1)}
              aria-label="Scroll categories right"
              data-testid="filterbar-scroll-right"
              className="hidden lg:flex absolute right-0 top-1/2 -translate-y-1/2 h-10 w-10 items-center justify-center rounded-full bg-[#e6f2ff] text-[#1a4d7a] hover:bg-[#d0e7ff] shadow-sm hover:shadow transition-all z-20 shrink-0"
              style={{ fontFamily: "'Outfit', sans-serif" }}
            >
              <ChevronRight className="h-5 w-5" strokeWidth={2.5} />
            </button>
          )}
        </div>
        </div>

      </div> {/* End Filter Buttons */}
    </div>
  );
};

// React.memo wrapper — `onFilterChange` is recreated on every parent
// render (inline arrow in ExperiencesPage), so default shallow equality
// would never short-circuit. We compare the data props that matter and
// always treat onFilterChange as stable; the callback closure reads the
// latest filters via setState anyway.
export const FilterBar = React.memo(FilterBarInner, (prev, next) => {
  if (prev.filters !== next.filters) return false;
  if (prev.categories !== next.categories) return false;
  if (prev.availableLanguages !== next.availableLanguages) return false;
  if (prev.clearAllSignal !== next.clearAllSignal) return false;
  return true;
});
